import { useEffect, useState, useMemo, useCallback, Fragment } from 'react'
import { Button, Table, Pagination } from "react-bootstrap"
import { matchSorter } from 'match-sorter';

import { boolToIcon } from '../../services/commonParse.js';
import { createColumn, createIndexList, createRowObj, createTableWithColumn, filterTable, lenTab, sliceTable } from "../../services/tableUtils.js"

// Table Display of the contents of the database You will need to pass in the
// following:
//
// @tableDate: the target display data. This should be a table in the
// columnar format compatible with what is defined by the tableUtils.js
//
// @columnLayout: an array of props for how each column in the table should
// be create and display. The following fields can be used to define how the
// dataset should be used:
//
// - Header: The title for the column to be displayed
//
//  - Cell: How the cell content should should be displayed. This should be a
//  single function that takes the row object as the input, and return the
//  display content of the display content.
//
//  - CellStyle (optional): How the cell content for individual cells should
//  be stylelized. This should be a single function that tkes the row object as
//  the input, and return a object that can be passed to the `style` item of
//  the JSX tags.
//
//  - Filter (optional): How to filter the table based on the content of the
//  row. This should be a object with the following fields:
//
// ---   filterState: A single useState react variable dictating how the
// filter should filter items
//
// --- filterDefault: a value to set the default filterState
//
// ---   executeArgs: A dictionary that is used as the content arguments of
// the following ereqyured functions
//
// --- headerContent: A function that returns how the filter setting can be
// modified in the header item
//
// ---  runFilter: A function that recieves the input table data and returns
// a filter and ordered version.

const PaginateTable = ({ tableData, columnLayout, entriesPerPage = 10 }) => {
  const [tableForDisplay, setTableForDisplay] = useState([]); // reduced table that is actually used to display items

  // Additional items required for pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  // Items that have filtering methods applied
  const filterList = [];
  const filterDep = [tableData];
  const filterRefresh = [tableData];

  // Running the filter on the display data
  for (const column of columnLayout) {
    if ('Filter' in column) {
      filterList.push(column.Filter);
      filterDep.push(column.Filter.filterState[0]);
      if (!('textBase' in column.Filter))
        filterRefresh.push(column.Filter.filterState[0])
    }
  }

  useEffect(() => { // Calling the either the filter values or the main filter items are updated
    let newTable = createTableWithColumn(tableData, "score", () => 1.0)
    filterList.forEach((filter) => {
      if (filter.filterState[0] !== null)
        newTable = filter.runFilter({ tableData: newTable, filterVal: filter.filterState[0], ...filter.executeArgs })
    })
    setTableForDisplay(newTable);
    let newPageCount = 'id' in newTable ? Number(newTable.id.length) / Number(entriesPerPage) : 1;
    newPageCount = newPageCount > Math.round(newPageCount) ? Math.round(newPageCount) + 1 : Math.round(newPageCount);
    setPageCount(newPageCount);
    let newPageIndex = pageIndex;
    newPageIndex = newPageIndex >= newPageCount ? newPageCount - 1 : newPageIndex;
    newPageIndex = newPageIndex <= 0 ? 0 : newPageIndex
    setPageIndex(newPageIndex);
    // eslint-disable-next-line
  }, filterDep);

  const resetFilter = () => filterList.forEach((filter) => filter.filterState[1](filter.filterDefault));
  const clearFilter = () => filterList.forEach((filter) => filter.filterState[1](null));

  const displayStart = pageIndex * entriesPerPage;
  const displayEnd = displayStart + entriesPerPage;

  // For table Headers will will be passing in the entire table as this ensures
  // filter options are not  lost when other data filters are active. However,
  // this needs to be memoized to ensure that there isn't excessive
  // re-rendering that causes the filter input fields to loose focus. As the
  // header is rendered in a loop (and ReactJS is very aggressive with
  // re-rendering when using loops). We will only allow the filter to loose
  // focus when the main input table changes.
  //
  // eslint-disable-next-line
  const MemoTableHeaders = useMemo(() => { return <TableHeaders tableData={tableData} columnLayout={columnLayout} /> }, filterRefresh)

  return <Fragment>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div><ControlPage pageState={[pageIndex, setPageIndex]} pageCount={pageCount} /></div>
      <div style={{ justifyContent: "right" }} >
        <FilterControl showLen={lenTab(tableForDisplay)} totLen={lenTab(tableData)} resetAction={resetFilter} clearAction={clearFilter} />
      </div>
    </div><br />
    <Table striped bordered >
      {MemoTableHeaders}
      <TableBody tableSlice={sliceTable(tableForDisplay, displayStart, displayEnd)} columnLayout={columnLayout} />
    </Table>
  </Fragment>
}

const ControlPage = ({ pageState, pageCount }) => {
  // Control variables
  const RANGE = 5;
  const MAXIDX = pageCount - 1;
  const [pageIndex, setPageIndex] = pageState;
  const minIndex = (pageIndex - RANGE < 0) ? 0 : pageIndex - RANGE;
  const maxIndex = (pageIndex + RANGE > MAXIDX) ? MAXIDX : pageIndex + RANGE;
  const indexList = Array.from({ length: maxIndex - minIndex + 1 }).map((e, i) => minIndex + i);
  const isLeading = pageIndex === 0;
  const isTrailing = pageIndex === MAXIDX;

  return <div style={{ verticalAlign: "center" }}>
    <Pagination>
      <Pagination.First onClick={() => setPageIndex(0)} disabled={isLeading} />
      <Pagination.Prev onClick={() => setPageIndex(pageIndex - 1)} disabled={isLeading} />
      {minIndex > 0 && <Pagination.Ellipsis disabled={true} />}
      {indexList.map((v) => <Pagination.Item active={v === pageIndex} onClick={() => setPageIndex(v)} key={`paginate${v}`}>{v + 1}</Pagination.Item>)}
      {maxIndex < pageCount - 1 && <Pagination.Ellipsis disabled={true} />}
      <Pagination.Next onClick={() => setPageIndex(pageIndex + 1)} disabled={isTrailing} />
      <Pagination.Last onClick={() => setPageIndex(pageCount - 1)} disabled={isTrailing} />
      <Pagination.Item style={{ marginLeft: "1em", marginRight: "1em" }} active>Page {pageCount === 0 ? 0 : pageIndex + 1}/{pageCount}</Pagination.Item>
    </Pagination>
  </div>
}

const FilterControl = ({ showLen, totLen, resetAction, clearAction }) => {
  return <div style={{ textAlign: 'right', verticalAlign: "center" }}>
    <span style={{ marginRight: "1.5em" }}>
      Filtered: {showLen} entries
      (Total: {totLen} entries)
    </span>
    <Button variant="info" onClick={resetAction}>Reset filters</Button>
    <Button variant="secondary" onClick={clearAction}>Disable all filters</Button>
  </div>
}

const TableHeaders = ({ tableData, columnLayout }) => {
  const HeaderFilter = ({ column }) => {
    if (!('id' in tableData)) return <></>;
    if (!('Filter' in column)) return <></>;
    const filter = column.Filter;
    return <><br />{column.Filter.headerContent({ tableData, ...filter.executeArgs, filterState: filter.filterState })}</>;
  }

  return <thead><tr>
    {columnLayout.map((column, idx) => {
      return <th style={{ textAlign: 'center', verticalAlign: 'top' }} key={`tableHeader${idx}`}>
        {column.Header} <HeaderFilter column={column} />
      </th>
    })}
  </tr></thead>
}

const TableRow = ({ rowObject, columnLayout, rowIdx }) => {
  return <tr key={`tableRowInternal${rowIdx}`}>
    {columnLayout.map((col, idx) => {
      return <td key={`tableXXX-${rowIdx}-${idx}`} style={'CellStyle' in col ? col.CellStyle(rowObject) : {}}>
        <>{col.Cell(rowObject)}</>
      </td>
    }
    )}
  </tr>
}

const TableBody = ({ tableSlice, columnLayout }) => {
  return <tbody>
    {createIndexList(tableSlice).map((idx) => <TableRow rowObject={createRowObj(tableSlice, idx)} columnLayout={columnLayout} rowIdx={idx} key={`tableRow${idx}`} />)}
  </tbody>
}

/** Commonly used filter method */

// Filtering the table content by category. You will need to provide a function
// of how to make the categorical column
export const CategoryFilter = (makeCat, filterDefault = "") => {
  return {
    filterState: useState(filterDefault),
    filterDefault: filterDefault,
    executeArgs: { makeCat }, // Additional arguments that need to be passed to construct the category selector
    headerContent: CategoryFilterHeader,
    runFilter: runCategoryFilter,
  };
}

const CategoryFilterHeader = ({ tableData, filterState, makeCat }) => {
  const [categoryList, setCategoryList] = useState([]);
  const [filterVal, setFilterVal] = filterState;
  useEffect(() => { // Update the category each time the tableDate is updated.
    setCategoryList([...new Set(createColumn(tableData, makeCat))]);
  }, [tableData, makeCat]);
  return <select
    value={filterVal === null ? "" : filterVal}
    onChange={e => { setFilterVal(e.target.value); }}
  >
    <option value="">(All)</option>
    {categoryList.map((val) => <option value={String(val)} key={`selection${String(val)}`}>{String(val)}</option>)}
  </select>
}

const runCategoryFilter = ({ tableData, filterVal, makeCat }) => {
  if (filterVal === "") { return tableData }
  const computeItemScore = (cat) => {
    if (filterVal === "null") return cat === null;
    return filterVal === cat;
  }
  // Avoid making dubplicats
  const catColumn = createColumn(tableData, makeCat);
  const newScore = createIndexList(tableData).map((idx) => {
    return tableData.score[idx] * computeItemScore(catColumn[idx]);
  })
  return filterTable(tableData, (_, idx) => newScore[idx] > 0);
}

// Filtering on true/false/null. This needs to be slightly different from the
// category method as the options require values to be strings, so explicitly
// casing will be required
export const BooleanFilter = (makeCat, filterDefault = "") => {
  return {
    filterState: useState(filterDefault),
    filterDefault: filterDefault,
    executeArgs: { makeCat }, // Additional arguments that need to be passed to construct the category selector
    headerContent: BooleanFilterHeader,
    runFilter: runBooleanFilter,
  };
}

const BooleanFilterHeader = ({ tableData, filterState, makeCat }) => {
  const [categoryList, setCategoryList] = useState([]);
  const [filterVal, setFilterVal] = filterState;
  useEffect(() => { // Update the category each time the tableDate is updated.
    setCategoryList([...new Set(createColumn(tableData, makeCat))]);
  }, [tableData, makeCat]);
  return <div style={{ verticalAlign: "middle", display: "inline" }}>
    Current: {filterVal}
    <div><select
      value={filterVal === null ? "" : filterVal}
      onChange={(e) => setFilterVal(e.target.value)}
    >
      <option value="">(All)</option>
      {categoryList.map((val) => <option value={String(val)} key={`selection${String(val)}`}>{val === null ? "(null)" : boolToIcon(val)}</option>)}
    </select></div>
  </div >
}

const runBooleanFilter = ({ tableData, filterVal, makeCat }) => {
  if (filterVal === "") return tableData;
  const computeItemScore = (bool) => {
    return String(bool) === String(filterVal);
  }
  const boolCol = createColumn(tableData, makeCat);
  const newScore = createIndexList(tableData).map((idx) => {
    return tableData.score[idx] * computeItemScore(boolCol[idx]);
  })
  return filterTable(tableData, (item, idx) => newScore[idx] > 0);
}

// Filtering based on a fuzzy text finder. You will need to a functions that
// takes a row instance and returns an array of strings used to match the fuzzy text.
export const FuzzyTextFilter = (makeTextMatch) => {
  return {
    filterState: useState(""), // Default should always be an empty string
    filterDefault: "",
    executeArgs: { makeTextMatch },
    headerContent: FuzzyTextHeader,
    runFilter: runFuzzyTextFilter,
    textBase: true
  }
}

// As fuzzy matching is computationally expensive, we will need to wrap the edit in a debound box
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const FuzzyTextHeader = ({ filterState }) => { // TODO: do not loost focus when filter is executed
  const [filterVal, setFilterVal] = filterState
  const [displayText, setDisplayText] = useState(filterVal); // Display item

  // eslint-disable-next-line
  const handleInputChange = useCallback(debounce((item) => { setFilterVal(item); }, 200), []);
  return <input
    className="form-control"
    value={displayText === null ? "" : displayText}
    onChange={(e) => { setDisplayText(e.target.value); handleInputChange(e.target.value || null); }}
    placeholder="Search for string..."
  />
}

const runFuzzyTextFilter = ({ tableData, filterVal, makeTextMatch }) => {
  if (!filterVal || filterVal === null) return tableData
  const computeItemScore = (textList) => {
    return (matchSorter(textList, filterVal).length > 0)
  }
  const textCols = createColumn(tableData, makeTextMatch);
  const newScore = createIndexList(tableData).map((idx) => {
    return tableData.score[idx] * computeItemScore(textCols[idx])
  })
  return filterTable(tableData, (_, idx) => newScore[idx] > 0);
}

// Additioanl Pass fail Filtering, the passed function should be how to
// generate the pass/fail condition, as well as the display title. And
// addtional flag can be set if the filter is active by default (false if not
// spcified)
export const PassFailFilter = (makePassFail, title, active = false) => {
  return {
    filterState: useState(active), // Default should always be an empty string
    filterDefault: active,
    executeArgs: { makePassFail, title },
    headerContent: PassFailHeader,
    runFilter: runPassFailFilter,
  }
}

const PassFailHeader = ({ filterState, title }) => {
  const [filterVal, setFilterVal] = filterState;
  return <div>
    {title}
    <input type="checkbox" checked={filterVal} onClick={() => setFilterVal(!filterVal)} readOnly />
  </div>
}

const runPassFailFilter = ({ tableData, filterVal, makePassFail }) => {
  if (filterVal === null) return tableData;
  const computeItemScore = (pf) => {
    if (!filterVal) return 1;
    return pf;
  }
  const pfCols = createColumn(tableData, makePassFail);
  const newScore = createIndexList(tableData).map((idx) => {
    return tableData.score[idx] * computeItemScore(pfCols[idx])
  })
  return filterTable(tableData, (_, idx) => newScore[idx] > 0);
}

export default PaginateTable;
